export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value: string): boolean => UUID_REGEX.test(value.trim());

/**
 * Helper for RecordIdInput callers: returns the final id to persist,
 * auto-generating one when the user left the field blank.
 */
export const resolveCreatedId = (rawValue: string): string => {
  const trimmed = rawValue.trim();
  return trimmed || crypto.randomUUID();
};

/**
 * Prefix used on frontend-generated ids for records that exist only in the
 * local draft and have not yet been persisted by the backend. Mirrors the
 * convention used in the annotations module so Redux state and dev tools
 * make it obvious which records are temporary.
 */
export const TEMP_ID_PREFIX = "temp_";

export const newTempId = (): string =>
  `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;

export const isTempId = (value: unknown): boolean =>
  typeof value === "string" && value.startsWith(TEMP_ID_PREFIX);

/**
 * Recursively walks any plain object/array structure and replaces every `id`
 * field that has the temp-id prefix with `null`. Used at the API send
 * boundary so backend DTOs (typed as UUID) never see the prefix — backend
 * treats null id as "create new", which matches the annotations pattern.
 */
export const stripTempIds = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(stripTempIds) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === "id" && isTempId(v)) {
        out[k] = null;
      } else {
        out[k] = stripTempIds(v);
      }
    }
    return out as T;
  }
  return value;
};
